import { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import { dateFns, LAST_SEVEN_DAYS } from 'constants/index'
import useHsmReport from 'mutations/useHsmReport'
import { getTenantId } from 'utils/token/token'
import { getCurrentEpochMinutesPrecise, getYesterday, transformEpochToDate } from 'utils/dateToEpoch'
import useGetSettings from 'queries/use-settings'
import { HsmReportsContext } from './context'

function HsmReportsProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [isToday, setIsToday] = useState(false)
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false)

  const [filterType, setFilterType] = useState('WEEK')
  const [notification, setNotification] = useState(0)

  const [totalDispatchMessages, setTotalDispatchMessages] = useState(0)
  const [totalSentMessages, setTotalSentMessages] = useState(0)
  const [totalReceivedMessages, setTotalReceivedMessages] = useState(0)
  const [totalReadMessages, setTotalReadMessages] = useState(0)
  const [totalFailedMessages, setTotalFailedMessages] = useState(0)
  const [totalRepliedMessages, setTotalRepliedMessages] = useState(0)
  const [totalBillableMessages, setTotalBillableMessages] = useState(0)
  const [csvPayload, setCsvPayload] = useState({})

  const [dataGraph, setDataGraph] = useState([])
  const [dataTable, setDataTable] = useState([])

  const [originFilter, setOriginFilter] = useState(0)
  const [senderFilter, setSenderFilter] = useState([])
  const [categoryFilter, setCategoryFilter] = useState({ isfetching: true })

  const [periodFilter, setPeriodFilter] = useState(LAST_SEVEN_DAYS)

  const [isUpdatedFilter, setIsUpdatedFilter] = useState(true)
  const [initialDateTime, setInitialDateTime] = useState()
  const [endDateTime, setEndDateTime] = useState()
  const { mutateAsync, isLoading } = useHsmReport()

  const { data: settingsData, isFetching } = useGetSettings()

  const setOpenSnackbar = (msg) => {
    setIsOpen(true)
    setMessage(msg)
  }

  const setCloseSnackbar = () => {
    setIsOpen(false)
    setMessage('')
  }

  const isTodayOrNotToday = useCallback(() => {
    const initial = new Date(transformEpochToDate(initialDateTime)).getDate()
    const final = new Date(transformEpochToDate(endDateTime)).getDate()

    if (initial === final) {
      setIsToday(true)
    } else {
      setIsToday(false)
    }
  }, [endDateTime, initialDateTime])

  const toggleStateSidebar = () => {
    setSidebarIsOpen((oldState) => !oldState)
  }

  const payloadToSend = useMemo(() => {
    const categories = categoryFilter
    const tenantId = getTenantId()

    let payload = null

    if (filterType === 'TODAY' && isUpdatedFilter) {
      payload = {
        startDateTicks: periodFilter,
        endDateTicks: getCurrentEpochMinutesPrecise(),
        origin: originFilter,
        senders: senderFilter,
      }
    }
    if (filterType === 'WEEK' || filterType === 'MONTH') {
      payload = {
        startDateTicks: periodFilter,
        endDateTicks: getYesterday(),
        origin: originFilter,
        senders: senderFilter,
      }
    }

    if (filterType === 'INITIAL_DATE') {
      isTodayOrNotToday()
      payload = {
        startDateTicks: initialDateTime,
        endDateTicks: endDateTime,
        origin: originFilter,
        senders: senderFilter,
      }
    }
    if (filterType === 'FINAL_DATE') {
      isTodayOrNotToday()
      payload = {
        startDateTicks: initialDateTime,
        endDateTicks: endDateTime,
        origin: originFilter,
        senders: senderFilter,
      }
    }

    if (payload) {
      Object.assign(payload, { categories, tenantId })
    }

    return payload
  }, [
    categoryFilter,
    endDateTime,
    filterType,
    initialDateTime,
    isTodayOrNotToday,
    originFilter,
    periodFilter,
    senderFilter,
    isUpdatedFilter,
  ])

  const isSameDay = useMemo(() => {
    if (filterType === 'INITIAL_DATE' || filterType === 'FINAL_DATE') {
      return dateFns.isSameDay(
        new Date(payloadToSend.startDateTicks * 1000),
        new Date(payloadToSend.endDateTicks * 1000),
      )
    }
    return false
  }, [filterType, payloadToSend?.endDateTicks, payloadToSend?.startDateTicks])

  const fetchDataByFilters = useCallback(async () => {
    try {
      const response = await mutateAsync(payloadToSend)
      setDataTable(response.dispatchAggregation)
      setDataGraph(response.histogram)
      setTotalDispatchMessages(response.totalAggregationValues.dispatched)
      setTotalReadMessages(response.totalAggregationValues.readed)
      setTotalSentMessages(response.totalAggregationValues.sent)
      setTotalReceivedMessages(response.totalAggregationValues.received)
      setTotalFailedMessages(response.totalAggregationValues.failed)
      setTotalRepliedMessages(response.totalAggregationValues.replied)
      setTotalBillableMessages(response.billed)
    } catch (error) {
      console.error(error)
    } finally {
      setCsvPayload(payloadToSend)
      setIsUpdatedFilter(false)
    }
  }, [mutateAsync, payloadToSend])

  const fetchReportData = useCallback(() => {
    setIsUpdatedFilter(true)
  }, [])

  useEffect(() => {
    if (!isFetching && settingsData?.data) setCategoryFilter(settingsData.data?.canUseExternalHSM ? [] : null)
  }, [isFetching, settingsData])

  useEffect(() => {
    if (!categoryFilter?.isfetching && isUpdatedFilter) {
      fetchDataByFilters()
    }
  }, [categoryFilter, fetchDataByFilters, isUpdatedFilter])

  const HsmReportsProviderValue = useMemo(
    () => ({
      state: {
        isOpen,
        message,
        totalDispatchMessages,
        totalSentMessages,
        totalReceivedMessages,
        totalReadMessages,
        totalFailedMessages,
        totalBillableMessages,
        totalRepliedMessages,
        dataGraph,
        dataTable,
        notification,
        filterType,
        csvPayload,
        isLoadingGraph: isLoading,
        isToday,
        sidebarIsOpen,
        categoryFilter,
        senderFilter,
        originFilter,
        isSameDay,
      },
      actions: {
        setOpenSnackbar,
        setCloseSnackbar,
        setNotification,
        setFilterType,
        setOriginFilter,
        setSenderFilter,
        setPeriodFilter,
        setInitialDateTime,
        setEndDateTime,
        setIsToday,
        toggleStateSidebar,
        setCategoryFilter,
        fetchReportData,
      },
    }),
    [
      categoryFilter,
      dataGraph,
      dataTable,
      filterType,
      isOpen,
      isToday,
      message,
      notification,
      originFilter,
      senderFilter,
      sidebarIsOpen,
      totalDispatchMessages,
      totalFailedMessages,
      totalReadMessages,
      totalReceivedMessages,
      totalSentMessages,
      totalRepliedMessages,
      totalBillableMessages,
      isLoading,
      csvPayload,
      fetchReportData,
      isSameDay,
    ],
  )

  return <HsmReportsContext.Provider value={HsmReportsProviderValue}>{children}</HsmReportsContext.Provider>
}

HsmReportsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default HsmReportsProvider
